import { Component, computed, effect, signal, input } from "@angular/core";
import { take } from "rxjs";
import { FieldTree, FormField } from "@angular/forms/signals";
import { defaultEstateTypes, EstateTypesFilterModel } from "../search-filter-estate-types/search-filter-estate-types.model";
import { SearchViewOptionsFilterModel } from "./search-filter-view-options.model";

import { inject } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { EvTooltipModule } from "ev-tooltip";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { Auth0UserProfileModel } from "src/util/auth0/models/auth0-user-profile.model";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { MarketStatisticsHelperComponent } from "./market-statistics-helper/market-statistics-helper.component";
import { PeriodDefinitionType } from "@models/map/market-filter.model";

@Component({
    selector: "search-filter-view-options",
    imports: [FormField, EvTooltipModule, MarketStatisticsHelperComponent],
    templateUrl: "./search-filter-view-options.component.html",
    styleUrl: "./search-filter-view-options.component.scss",
})
export class SearchFilterViewOptionsComponent {
    readonly filterForm = input.required<FieldTree<SearchViewOptionsFilterModel>>();
    readonly estateTypesForm = input<FieldTree<EstateTypesFilterModel>>();
    protected readonly viewOptions = computed(() => this.filterForm()().value());
    private readonly authService = inject(EvAuthorizationService);
    private readonly auth0Service = inject(AuthService);
    showPeriodHelper: boolean;
    showJudicialTransferTooltip: boolean;
    showMarketSalesTooltip: boolean;
    showWithdrawnTooltip: boolean;
    showIsOwnedByCompanyTooltip: boolean;
    showUncertainPriceTooltip: boolean;
    hasShowEstateAgentCompanySales = this.authService.hasPermissionSignal(EVServiceType.EvWeb_ShowEstateAgentCompanySales, false, false);
    user: Auth0UserProfileModel;
    protected selectedOption = signal<string>("InMarket");
    private readonly estateAgentId = signal<number | null>(null);

    constructor() {
        // Fetch the user once for the label/visibility of the "only estate agency sales" option.
        // This must not touch the form, otherwise merely opening the panel would dirty the filter.
        this.auth0Service.user$.pipe(take(1)).subscribe((x) => {
            this.user = x;
            this.estateAgentId.set(
                x["https://eiendomsverdi.no/evEstateAgentNameHeadOffice"]
                    ? x["https://eiendomsverdi.no/evEstateAgentIDHeadOffice"]
                    : x["https://eiendomsverdi.no/evEstateAgentID"],
            );
        });

        effect(() => {
            if (this.selectedOption() != PeriodDefinitionType[this.filterForm().periodDefinitionType().value()]) {
                this.selectedOption.set(PeriodDefinitionType[this.filterForm().periodDefinitionType().value()]);
            }
            const targetId = this.filterForm().includeOnlyEstateAgencySales().value() ? this.estateAgentId() : null;
            if (this.filterForm().estateAgentHeadOfficeId().value() !== targetId) {
                this.filterForm().estateAgentHeadOfficeId().value.set(targetId);
            }
        });
    }

    selectOption(option: "InMarket" | "Registered" | "Sold") {
        this.selectedOption.set(option);
        this.filterForm().periodDefinitionType().value.set(option === "InMarket" ? 3 : option === "Registered" ? 1 : option === "Sold" ? 2 : null);
    }

    toggleExpanded(): void {
        this.filterForm().expanded().value.set(!this.filterForm().expanded().value());
    }

    setSitesOnly(sitesOnly: boolean): void {
        this.filterForm().isSitePrice().value.set(sitesOnly);
        // Unselect all estate types if sites only is selected
        if (sitesOnly) {
            this.estateTypesForm().estateTypes().value.update((estateTypes) => {
                return estateTypes.map((estateType) => {
                    return { ...estateType, selected: false };
                });
            });
        } else {
            this.estateTypesForm().estateTypes().value.set(defaultEstateTypes);
        }
    }

    toggleJudicialTransfers(include: boolean): void {
        this.filterForm().includeJudicialTransfers().value.set(!include);
    }

    toggleAgentSales(include: boolean): void {
        const includeAgentSales = !include;
        this.filterForm().includeAgentSales().value.set(includeAgentSales);
        if (includeAgentSales) return;

        this.filterForm().includeWithDrawnPrices().value.set(false);
        this.filterForm().isForSale().value.set(false);
        this.filterForm().includeOnlyEstateAgencySales().value.set(false);
    }

    toggleForSaleOnly(include: boolean): void {
        if (this.filterForm().isForSale().disabled()) return;
        this.filterForm().isForSale().value.set(!include);
    }

    toggleOwnedByCompany(include: boolean): void {
        this.filterForm().isOwnedByCompany().value.set(!include);
    }

    toggleWithDrawnPrices(include: boolean): void {
        if (this.filterForm().includeWithDrawnPrices().disabled()) return;
        this.filterForm().includeWithDrawnPrices().value.set(!include);
    }

    toggleUncertainPrice(include: boolean): void {
        this.filterForm().includeUnCertainPrices().value.set(!include);
    }

    toggleEstateAgencySales(include: boolean): void {
        if (this.filterForm().includeOnlyEstateAgencySales().disabled()) return;
        this.filterForm().includeOnlyEstateAgencySales().value.set(!include);
    }

    getEstateAgentName(user: Auth0UserProfileModel) {
        return user["https://eiendomsverdi.no/evEstateAgentNameHeadOffice"]
            ? user["https://eiendomsverdi.no/evEstateAgentNameHeadOffice"]
            : user["https://eiendomsverdi.no/evEstateAgentName"];
    }
}
